<template>
  <div class="customize-brand-theme-container">
    <div class="theme-header-row">
      <div class="theme-header-col">
        <h4>Customize Brand Theme</h4>
      </div>
      <div
        v-if="selectedPlatform !== 'web'"
        class="theme-header-col theme-preview-heading-col"
        style="justify-content: flex-start"
      >
        <h4>Theme Preview</h4>
      </div>
    </div>

    <div class="theme-form-row">
      <!-- LEFT SIDE -->
      <div
        class="theme-form-col"
        :style="selectedPlatform === 'web' ? 'height: auto;' : ''"
      >
        <!-- WEB PLATFORM COLOR PICKERS -->
        <template v-if="selectedPlatform === 'web'">
          <color-picker
            colorRef="primary-col"
            colorId="primary-col"
            :defaultValue="primary"
            label="Primary Color"
            @change="(val) => (primary = val)"
            ref="primary-colour"
            class="mb-4"
          />
          <color-picker
            colorRef="primary-gradient-col"
            colorId="primary-gradient-col"
            :defaultValue="primaryDark"
            label="Secondary Color (Optional)"
            @change="(val) => (primaryDark = val)"
            ref="primary-gradient"
            class="mb-4"
          />
          <color-picker
            colorRef="primary-light-col"
            colorId="primary-light-col"
            :defaultValue="primaryLight"
            label="Primary Light"
            @change="(val) => (primaryLight = val)"
            ref="primary-light"
            class="mb-4"
          />
          <color-picker
            colorRef="primary-text-col"
            colorId="primary-text-col"
            :defaultValue="primaryText"
            label="Primary Text"
            @change="(val) => (primaryText = val)"
            ref="primary-text"
            class="mb-4"
          />
        </template>

        <!-- NON-WEB PLATFORMS: original mobile/seller/agent pickers -->
        <template v-else>
          <color-picker
            colorRef="primary-col"
            colorId="primary-col"
            :defaultValue="primary"
            label="Primary Color"
            @change="(val) => (primary = val)"
            ref="primary-colour"
            class="mb-4"
          />
          <color-picker
            v-if="selectedPlatform == 'seller'"
            colorRef="primary-col"
            colorId="primary-col"
            :defaultValue="secondary"
            label="Secondary Colour"
            @change="(val) => (secondary = val)"
            ref="secondary-colour"
            class="mb-4"
          />
          <color-picker
            v-if="selectedPlatform == 'seller'"
            colorRef="primary-col"
            colorId="primary-col"
            :defaultValue="backgroundColor"
            label="Background Colour"
            @change="(val) => (backgroundColor = val)"
            ref="bg-colour"
            class="mb-4"
          />
          <color-picker
            v-if="selectedPlatform !== 'seller'"
            colorRef="header-col"
            colorId="header-col"
            :defaultValue="primaryLight"
            label="Primary Light"
            @change="(val) => (primaryLight = val)"
            ref="header-primary"
            class="mb-4"
          />
          <color-picker
            v-if="selectedPlatform !== 'seller'"
            colorRef="primary-dark-col"
            colorId="primary-dark-col"
            :defaultValue="primaryDark"
            label="Primary Dark"
            @change="(val) => (primaryDark = val)"
            ref="primary-dark-colour"
            class="mb-4"
          />
          <color-picker
            v-if="selectedPlatform !== 'seller'"
            colorRef="font-col"
            colorId="font-col"
            :defaultValue="headerFont"
            label="Header Font"
            @change="(val) => (headerFont = val)"
            ref="font"
            class="mb-4"
          />
          <color-picker
            colorRef="footer-normal-col"
            colorId="footer-normal-col"
            :defaultValue="footerNormal"
            label="Footer Normal Colour"
            @change="(val) => (footerNormal = val)"
            ref="footer-normal-colour"
            class="mb-4"
          />
          <color-picker
            colorRef="footer-sel-col"
            colorId="footer-sel-col"
            :defaultValue="footerSelection"
            label="Footer Selection Colour"
            @change="(val) => (footerSelection = val)"
            ref="footer-sel-colour"
            class="mb-4"
          />
        </template>

        <!-- Brand Logo -->
        <div class="flex flex-column justify-center align-items-center">
          <h4 class="mb-2">Brand Logo</h4>
          <single-image
            height="150px"
            width="150px"
            @change="(val) => $emit('update-logo', val)"
            :id="'appLogo'"
            :url="appLogo"
            ref="appLogo"
            :label="$t('buildGeneration.browseForBigImage')"
          />
        </div>
      </div>

      <!-- RIGHT SIDE PREVIEW -->
      <div
        class="theme-preview-col"
        :style="selectedPlatform !== 'web' ? 'height: 760px;' : ''"
      >
        <template v-if="selectedPlatform === 'web'">
          <color-picker
            colorRef="footer-bg"
            colorId="footer-bg"
            :defaultValue="footerBgColor"
            label="Footer Background Color"
            @change="(val) => (footerBgColor = val)"
            ref="footer-bg-color"
            class="mb-4"
          />
          <color-picker
            colorRef="footer-text"
            colorId="footer-text"
            :defaultValue="footerTextColor"
            label="Footer Text Color"
            @change="(val) => (footerTextColor = val)"
            ref="footer-text-color"
            class="mb-4"
          />
        </template>
        <h4 v-if="selectedPlatform === 'web'" class="theme-preview-heading">
          Theme Preview
        </h4>
        <div class="theme-preview-inner web-preview-shifted">
          <div class="mobile-preview-wrapper" v-if="selectedPlatform === 'mobile'">
            <mobile-view
              screen="home"
              :primary-color="primary"
              :statusBar="primary"
              :headerTop="primaryDark"
              :app-icon="appLogo"
              :app-name="appName"
              :header-font="headerFont"
              :footer-normal="footerNormal"
              :footer-selection="footerSelection"
            />
          </div>
          <div class="mobile-preview-wrapper" v-if="selectedPlatform === 'seller'">
            <mobile-view
              screen="sellerHome"
              :primary-color="primary"
              :statusBar="primary"
              :app-icon="appLogo"
              :app-name="appName"
              :secondary="secondary"
              :appBgColour="backgroundColor"
              :footer-normal="footerNormal"
              :footer-selection="footerSelection"
            />
          </div>
          <div class="mobile-preview-wrapper" v-if="selectedPlatform === 'agent'">
            <mobile-view
              screen="agentHome"
              :primary-color="primary"
              :statusBar="primary"
              :headerTop="primaryDark"
              :app-icon="appLogo"
              :app-name="appName"
              :header-font="headerFont"
              :footer-normal="footerNormal"
              :footer-selection="footerSelection"
            />
          </div>
          <div class="laptop-preview-wrapper" v-if="selectedPlatform === 'web'">
            <!-- <toys-laptop-view
              v-if="market.name === 'Sports & Toys'"
              :primary-color="primary"
              :headerTop="primaryDark"
              :tagLine="tagLine"
              :primaryTextColor="primaryText"
              :statusBar="primary"
              :app-icon="appLogo"
              :app-name="appName"
              :primary-light-color="primaryLight"
              :btn-colour="primaryLight"
              :footerBgColor="footerBgColor"
              :footerTextColor="footerTextColor"
            />
            <beauty-laptop-view
              v-else-if="market.name === 'Beauty & Personal care'"
              :primary-color="primary"
              :headerTop="primaryDark"
              :primaryTextColor="primaryText"
              :tagLine="tagLine"
              :statusBar="primary"
              :app-icon="appLogo"
              :app-name="appName"
              :primary-light-color="primaryLight"
              :btn-colour="primaryLight"
              :footerBgColor="footerBgColor"
              :footerTextColor="footerTextColor"
            />
            <fruits-laptop-view
              v-else-if="market.name === 'Fruit & Vegetables'"
              :primary-color="primary"
              :tagLine="tagLine"
              :primaryGradient="primaryDark"
              :primaryTextColor="primaryText"
              :statusBar="primary"
              :app-icon="appLogo"
              :app-name="appName"
              :primary-light-color="primaryLight"
              :btn-colour="primaryLight"
              :footerBgColor="footerBgColor"
              :footerTextColor="footerTextColor"
            />
            <grocery-laptop-view
              v-else-if="market.name === 'Groceries'"
              :primary-color="primary"
              :tagLine="tagLine"
              :primaryGradient="primaryDark"
              :primaryTextColor="primaryText"
              :statusBar="primary"
              :app-icon="appLogo"
              :app-name="appName"
              :primary-light-color="primaryLight"
              :btn-colour="primaryLight"
              :footerBgColor="footerBgColor"
              :footerTextColor="footerTextColor"
            />
            <food-laptop-view
              v-else-if="market.name === 'Food & Restaurants'"
              :primary-color="primary"
              :tagLine="tagLine"
              :primaryGradient="primaryDark"
              :primaryTextColor="primaryText"
              :statusBar="primary"
              :app-icon="appLogo"
              :app-name="appName"
              :primary-light-color="primaryLight"
              :btn-colour="primaryLight"
              :footerBgColor="footerBgColor"
              :footerTextColor="footerTextColor"
            /> -->
            <laptop-view
              :primary-color="primary"
              :headerTop="primaryDark"
              :primaryTextColor="primaryText"
              :statusBar="primary"
              :app-icon="appLogo"
              :app-name="appName"
              :primary-light-color="primaryLight"
              :btn-colour="primaryLight"
              :footerBgColor="footerBgColor"
              :footerTextColor="footerTextColor"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Footer Button Slot -->
    <div class="footer-btn-row">
      <slot name="footer-button" :data="ThemeData"></slot>
    </div>
  </div>
</template>

<script>
import ColorPicker from "@/components/FormElements/ColorPicker.vue";
import SingleImage from "@/components/Common/Image.vue";
import mobileView from "@/views/profile/mobileView.vue";
import laptopView from "@/views/profile/laptopView.vue";
import toysLaptopView from "@/views/profile/LaptopView/toysLaptopView.vue";
import fruitsLaptopView from "@/views/profile/LaptopView/fruitsLaptopView.vue";
import groceryLaptopView from "@/views/profile/LaptopView/groceryLaptopView.vue";
import foodLaptopView from "@/views/profile/LaptopView/foodLaptopView.vue";
import beautyLaptopView from "@/views/profile/LaptopView/beautyLaptopView.vue";
import tinycolor from "tinycolor2";
import { mapState } from "vuex";

export default {
  name: "customizeBrandTheme",
  data() {
    return {
      primary: "#2a8750",
      secondary: "#15E406",
      backgroundColor: "#E6F0FF",
      primaryLight: "#ff6e34",
      primaryText: "#000000",
      primaryDark: "#2a8750", // gradient
      headerFont: "#262626",
      footerNormal: "#1e792b",
      footerSelection: "#484242",
      footerBgColor: "#2a8750", // footer color (secondary)
      footerTextColor: "#ffffff", // footer text (secondaryText)
      marketBgColor: "#f7f7f7",
      isSyncing: false,
      isInitializing: false,
    };
  },
  computed: {
    ...mapState({
      extendedList: (state) => state.tenant.extendedList,
    }),
    ThemeData() {
      if (this.selectedPlatform === "web") {
        return {
          primary: this.primary,
          primaryLight: this.primaryLight,
          primaryDark: this.primaryDark,
          primaryText: this.primaryText,
          footerBgColor: this.footerBgColor,
          footerTextColor: this.footerTextColor,
        };
      } else {
        return {
          primary: this.primary,
          primaryLight: this.primaryLight,
          primaryDark: this.primaryDark,
          headerFont: this.headerFont,
          footerNormal: this.footerNormal,
          footerSelection: this.footerSelection,
          footerBgColor: this.footerBgColor,
          footerTextColor: this.footerTextColor,
          marketBgColor: this.marketBgColor,
          secondary: this.secondary,
          borderColor: "#0000001A",
          backgroundColor: this.backgroundColor,
          // white: "#ffffff",
        };
      }
    },
  },
  components: {
    ColorPicker,
    SingleImage,
    mobileView,
    fruitsLaptopView,
    groceryLaptopView,
    laptopView,
    toysLaptopView,
    beautyLaptopView,
    foodLaptopView,
  },
  props: {
    selectedPlatform: { type: String, required: true },
    appName: { type: String, default: "" },
    tagLine: { type: String, default: "" },
    appLogo: { type: String, default: "" },
    market: { type: Object, default: () => ({}) },
  },
  watch: {
    "market.defaultTheme": {
      handler(theme) {
        if (theme && Object.keys(theme).length) {
          this.isInitializing = true;
          this.primary = theme.primary || this.primary;
          this.primaryDark = theme.primaryGradient || this.primaryDark;
          this.primaryText = theme.primaryText || this.primaryText;
          this.primaryLight = theme.primaryLight || this.primaryLight;
          this.footerBgColor = theme.secondary || this.footerBgColor;
          this.footerTextColor = theme.secondaryText || this.footerTextColor;
          this.isInitializing = false;
        }
      },
      immediate: true,
      deep: true,
    },
    selectedPlatform: {
      immediate: true,
      handler(newVal) {
        const platformConfig = this.extendedList.buildConfig?.[newVal]?.theme;

        if (platformConfig) {
          this.isInitializing = true;

          this.primary = platformConfig.primary || this.primary;
          this.primaryDark = platformConfig.primaryGradient || this.primaryDark;
          this.primaryText = platformConfig.primaryText || this.primaryText;
          this.primaryLight = platformConfig.primaryLight || this.primaryLight;
          this.footerBgColor = platformConfig.secondary || this.footerBgColor;
          this.footerTextColor = platformConfig.secondaryText || this.footerTextColor;

          this.isInitializing = false;
        }
      },
    },
    primary(newVal) {
      if (this.isInitializing) return;
      if (this.selectedPlatform === "web") {
        // For web: set gradient & footer color same as primary
        this.primaryDark = newVal;
        this.footerBgColor = newVal;
      } else {
        // Original lighten/darken sync logic for non-web
        if (!this.isSyncing) {
          this.isSyncing = true;
          this.primaryLight = tinycolor(newVal).lighten(50).toHexString();
          this.isSyncing = false;
        }
      }
    },
    primaryLight(newVal) {
      if (this.isInitializing) return;
      if (this.selectedPlatform !== "web") {
        if (!this.isSyncing) {
          this.isSyncing = true;
          this.primary = tinycolor(newVal).darken(50).toHexString();
          this.isSyncing = false;
        }
      }
    },
  },
};
</script>

<style scoped>
.customize-brand-theme-container {
  border: 1px solid #e3eaef;
  border-radius: 8px;
  padding: 16px;
  background: #fff;
  width: 100%;
}

.theme-header-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 0.5rem;
}

.theme-header-col {
  flex: 1;
  display: flex;
  align-items: flex-end;
}
.theme-preview-heading-col {
  justify-content: flex-start;
}
.theme-form-row {
  display: flex;
  gap: 28px;
  align-items: flex-start;
}
.theme-form-col {
  width: 50%;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e3eaef;
  padding: 24px 18px 18px 18px;
  box-sizing: border-box;
}

.theme-preview-col {
  width: 48%;
  background: #fafbfc;
  border-radius: 8px;
  border: 1px solid #e3eaef;
  padding: 24px 18px 18px 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
}

.theme-preview-inner {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100%;
}

.mobile-preview-wrapper {
  max-width: 320px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
}

.footer-btn-row {
  display: flex;
  justify-content: center;
  margin-top: 32px;
}

.laptop-preview-wrapper {
  max-width: 520px;
  width: 100%;
  margin: 32px auto 0;
  display: flex;
  justify-content: center;
  align-items: center;
  transform: scale(0.5);
  transform-origin: top center;
}

.theme-preview-heading {
  text-align: left;
  width: 100%;
  margin-bottom: 0.5rem;
}

/* Color Picker and Form Elements Responsiveness */
:deep(.color-picker-container) {
  width: 100%;
  max-width: 100%;
  margin-bottom: 16px;
}

:deep(.color-picker-input) {
  width: 100%;
  max-width: 100%;
}

:deep(.single-image-container) {
  width: 100%;
  max-width: 300px;
  margin: 0 auto;
}

/* Adjust spacing for form elements */
.mb-4 {
  margin-bottom: 1rem !important;
}

/* Tablet Responsive Styles */
@media (max-width: 1024px) {
  .theme-form-row {
    gap: 24px;
  }

  .theme-form-col,
  .theme-preview-col {
    padding: 16px;
  }

  .laptop-preview-wrapper {
    transform: scale(0.7);
  }

  :deep(.color-picker-container) {
    margin-bottom: 12px;
  }

  :deep(.single-image-container) {
    max-width: 250px;
  }
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .customize-brand-theme-container {
    padding: 12px;
  }

  .theme-header-row {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }

  .theme-form-row {
    flex-direction: column;
    gap: 16px;
  }

  .theme-form-col,
  .theme-preview-col {
    width: 100%;
    max-width: 100%;
    padding: 16px 12px;
  }

  .mobile-preview-wrapper {
    max-width: 280px;
  }

  .laptop-preview-wrapper {
    transform: scale(0.6);
    margin-top: 16px;
  }

  .footer-btn-row {
    margin-top: 24px;
  }

  :deep(.color-picker-container) {
    margin-bottom: 10px;
  }

  :deep(.color-picker-label) {
    font-size: 14px;
    margin-bottom: 4px;
  }

  :deep(.single-image-container) {
    max-width: 200px;
  }

  .flex.flex-column.justify-center.align-items-center {
    width: 100%;
    padding: 10px;
  }

  .flex.flex-column.justify-center.align-items-center h4 {
    font-size: 16px;
    margin-bottom: 8px;
  }
}

/* Small Mobile Devices */
@media (max-width: 480px) {
  .customize-brand-theme-container {
    padding: 8px;
  }

  .theme-form-col,
  .theme-preview-col {
    padding: 12px 8px;
  }

  .mobile-preview-wrapper {
    max-width: 240px;
  }

  .laptop-preview-wrapper {
    transform: scale(0.5);
  }

  :deep(.color-picker-container) {
    margin-bottom: 8px;
  }

  :deep(.color-picker-label) {
    font-size: 13px;
  }

  :deep(.single-image-container) {
    max-width: 150px;
  }

  .flex.flex-column.justify-center.align-items-center h4 {
    font-size: 15px;
  }
}
</style>
